import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { notifySearchFailure } from '../../../utils/searchFailureToast';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import rootStore, { IMobxRootState } from '../../../RootStore';
import {
    IActiveCountry,
    IBasicActivityRecord,
    INSIGHT_FEED_TYPE,
    VIEW,
} from '../../../interfaces';
import APIService from '../../../services/APIService';
import { PickerDrawer } from '../../Pickers/PickerDrawer';
import Insights from './Insights';
import { InsightWithDateString } from './InsightItem';
import { first, get, isEmpty } from 'lodash/fp';
import { MOMENTJS_DATE_DISPLAY_FORMAT } from '../../../constants';
import { useMatch, useNavigate } from 'react-router-dom';
import { E_TAB_FILTER, INSIGHTS_TYPE_BY_TAB } from './ActivityPageInterfaces';
import { getLastItemDate, downloadFromLink } from './activityUtils';
import useIsGQInternalUser from 'hooks/useIsGQInternalUser';

const TAB_BY_FEED_TYPE = Object.fromEntries(
    Object.entries(INSIGHTS_TYPE_BY_TAB).map(([tab, feedType]) => [feedType, tab])
) as Record<INSIGHT_FEED_TYPE, E_TAB_FILTER>;

type Props = {
    feedType?: INSIGHT_FEED_TYPE;
    selectedCountries?: number[];
    maxDate?: moment.Moment;
    minDate?: moment.Moment;
    licensedCountriesArr?: IActiveCountry[];
    searchString?: string;
    canShareInsights?: boolean;
    canDownloadInsightData?: boolean;
};

const PER_PAGE = 30;

const InsightsProvider = inject(
    ({ insightsFeedStore, countryStore, UserStore }: IMobxRootState) => ({
        selectedCountries: insightsFeedStore.selectedCountries,
        maxDate: insightsFeedStore.maxDate,
        minDate: insightsFeedStore.minDate,
        licensedCountriesArr: countryStore.countries.licensedCountriesArr,
        searchString: insightsFeedStore.searchString,
        canShareInsights: UserStore.geoquantFlagsPermission.canShareInsights,
        canDownloadInsightData:
            UserStore.geoquantFlagsPermission.canDownloadInsights,
    })
)(
    observer(
        ({
            feedType = INSIGHT_FEED_TYPE.Geoquant,
            selectedCountries,
            maxDate,
            minDate,
            licensedCountriesArr,
            searchString,
            canShareInsights,
            canDownloadInsightData,
        }: Props) => {
            const [insights, setInsights] = useState<IBasicActivityRecord[]>(
                []
            );
            const [loading, setLoading] = useState(true);
            const [loadFailed, setLoadFailed] = useState(false);
            const [hasMore, setHasMore] = useState(true);
            const mountedRef = useRef(true);
            const insightsRef = useRef<IBasicActivityRecord[]>([]);
            const loadingRef = useRef(false);
            const navigate = useNavigate();
            const updateInsights = useCallback(
                (newInsights: IBasicActivityRecord[]) => {
                    insightsRef.current = newInsights;
                    setInsights(newInsights);
                },
                []
            );
            const isGQInternalUser = useIsGQInternalUser();
            const fetchInsights = useCallback(
                async (reset: boolean) => {
                    if (loadingRef.current) return;
                    loadingRef.current = true;
                    setLoading(true);
                    setLoadFailed(false);
                    const current = reset ? [] : insightsRef.current;
                    if (reset) updateInsights([]);
                    try {
                        const maxDateParam =
                            getLastItemDate(
                                current,
                                TAB_BY_FEED_TYPE[feedType]
                            ) || maxDate?.format('YYYY-MM-DD');
                        let activities: IBasicActivityRecord[];
                        if (searchString) {
                            activities = await APIService.searchActivities(
                                TAB_BY_FEED_TYPE[feedType],
                                {
                                    maxDate: maxDateParam,

                                    limit: PER_PAGE,
                                    searchString,
                                }
                            ) as IBasicActivityRecord[];
                        } else {
                            activities = await APIService.getActivities({
                                feedTypes: [feedType],
                                limit: PER_PAGE,
                                maxDate: maxDateParam,
                                startDate: minDate?.format('YYYY-MM-DD'),
                            });
                        }
                        if (!mountedRef.current) return;
                        const updated = reset
                            ? activities
                            : [...current, ...activities];
                        updateInsights(updated);
                        setHasMore(activities.length >= PER_PAGE);
                    } catch (e) {
                        console.error('Failed to fetch insights:', e);
                        if (!mountedRef.current) return;
                        if (searchString) {
                            notifySearchFailure('insights', () => (rootStore.insightsFeedStore.searchString = ''));
                        } else {
                            setLoadFailed(true);
                        }
                    } finally {
                        loadingRef.current = false;
                        if (mountedRef.current) setLoading(false);
                    }
                },
                [maxDate, minDate, feedType, searchString, updateInsights]
            );
            useEffect(() => {
                updateInsights([]);
                setHasMore(true);
                fetchInsights(true);
            }, [maxDate, minDate, feedType, searchString]);
            useEffect(() => {
                return () => {
                    mountedRef.current = false;
                };
            }, []);
            const loadMore = useCallback(() => {
                if (!loadingRef.current && hasMore) {
                    fetchInsights(false);
                }
            }, [hasMore, fetchInsights]);
            const filteredInsights = useMemo(() => {
                if (isEmpty(selectedCountries)) return insights;
                return insights.filter(insight =>
                    insight.countries?.some(c => selectedCountries.includes(c))
                );
            }, [insights, selectedCountries]);
            const insightsWithDateString = useMemo(
                () =>
                    filteredInsights.map(insight => ({
                        ...insight,
                        dateString: moment(insight.date).format(
                            MOMENTJS_DATE_DISPLAY_FORMAT
                        ),
                    })),
                [filteredInsights]
            );
            const match = useMatch(`/${VIEW.RISK_INTELLIGENCE}/:insightsView/:id/`);
            const hasIdInUrl = !!match?.params?.id;
            const routeSegment =
                feedType === INSIGHT_FEED_TYPE.My ? 'my_insights' : 'insights';
            useEffect(() => {
                const ids = filteredInsights
                    .map(i => i._id)
                    .filter(Boolean) as string[];
                if (feedType === INSIGHT_FEED_TYPE.My) {
                    rootStore.insightsFeedStore.myCurrentIds = ids;
                } else {
                    rootStore.insightsFeedStore.currentIds = ids;
                }
            }, [filteredInsights, feedType]);
            useEffect(() => {
                return () => {
                    if (feedType === INSIGHT_FEED_TYPE.My) {
                        rootStore.insightsFeedStore.myCurrentIds = [];
                    } else {
                        rootStore.insightsFeedStore.currentIds = [];
                    }
                };
            }, [feedType]);
            useEffect(() => {
                if (!hasIdInUrl && !isEmpty(filteredInsights)) {
                    navigate(
                        `/${VIEW.RISK_INTELLIGENCE}/${routeSegment}/${get(
                            '_id',
                            first(filteredInsights)
                        )}`,
                        { replace: true }
                    );
                }
            }, [hasIdInUrl, filteredInsights, navigate, routeSegment]);
            const onInsightClick = useCallback(
                (id: string) => {
                    navigate(
                        `/${VIEW.RISK_INTELLIGENCE}/${routeSegment}/${id}`
                    );
                },
                [navigate, routeSegment]
            );

            const onEdit = useCallback(
                (insight: InsightWithDateString) => {
                    navigate(
                        `/${VIEW.CREATE_INSIGHT}/${VIEW.RISK_VIEW}/${insight._id}`
                    );
                },
                [navigate]
            );

            const onBroadcast = useCallback(
                async (
                    insight: InsightWithDateString,
                    sendEmail: boolean
                ) => {
                    try {
                        const updated = await APIService.shareActivitiy(
                            insight,
                            sendEmail
                        );
                        if (!mountedRef.current) return;
                        updateInsights(
                            insightsRef.current.map(i =>
                                i._id === updated._id
                                    ? { ...i, ...updated }
                                    : i
                            )
                        );
                    } catch (e) {
                        console.error('Failed to share insight:', e);
                    }
                },
                [updateInsights]
            );

            const onDownload = useCallback(
                (insight: InsightWithDateString) => {
                    const zipURL = get('zipURL', insight) as
                        | string
                        | undefined;
                    if (zipURL) {
                        try {
                            downloadFromLink(zipURL);
                        } catch (e) {
                            console.error('Failed to download insight:', e);
                        }
                    }
                },
                []
            );

            const onDelete = useCallback(
                async (insight: InsightWithDateString) => {
                    try {
                        await APIService.deleteActivity(insight._id);
                        if (!mountedRef.current) return;
                        updateInsights(
                            insightsRef.current.filter(
                                i => i._id !== insight._id
                            )
                        );
                    } catch (e) {
                        console.error('Failed to delete insight:', e);
                    }
                },
                [updateInsights]
            );
            return (
                <PickerDrawer
                    title={
                        feedType === INSIGHT_FEED_TYPE.My
                            ? 'My Insights'
                            : 'Insights'
                    }
                    side="left"
                    defaultOpen>
                    <Insights
                        insights={insightsWithDateString}
                        isLoading={loading}
                        loadFailed={loadFailed}
                        loadMore={loadMore}
                        licensedCountriesArr={licensedCountriesArr}
                        onInsightClick={onInsightClick}
                        maxDate={maxDate}
                        minDate={minDate}
                        selectedCountries={selectedCountries}
                        searchString={searchString}
                        onSetSearchString={str =>
                            (rootStore.insightsFeedStore.searchString = str)
                        }
                        removeCountry={countryId =>
                            rootStore.insightsFeedStore.removeCountry(countryId)
                        }
                        addCountry={countryId =>
                            rootStore.insightsFeedStore.addCountry(countryId)
                        }
                        toggleCountry={countryId =>
                            rootStore.insightsFeedStore.toggleCountry(countryId)
                        }
                        onDateRangeChange={(startDate, endDate) => {
                            rootStore.insightsFeedStore.minDate = startDate
                                ? moment(startDate)
                                : null;
                            rootStore.insightsFeedStore.maxDate = endDate
                                ? moment(endDate)
                                : null;
                        }}
                        onDateRangeReset={() =>
                            rootStore.insightsFeedStore.resetDateFilter()
                        }
                        activeId={match?.params?.id}
                        canShare={canShareInsights}
                        canDownloadData={canDownloadInsightData}
                        onEdit={onEdit}
                        onBroadcast={onBroadcast}
                        onDownload={onDownload}
                        onDelete={onDelete}
                        isGQInternalUser={isGQInternalUser}
                    />
                </PickerDrawer>
            );
        }
    )
);

export default InsightsProvider;